'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const SLIDE_MS = 5000;
const DELAY_MS = 1000;
const SWIPE_THRESHOLD_PX = 50;

type Options = {
	count: number;
	autoPlay: boolean;
	onManualAdvance?: () => void;
};

export function useSlideshow({ count, autoPlay, onManualAdvance }: Options) {
	const containerRef = useRef<HTMLElement | null>(null);
	const elapsedRef = useRef(0);
	const touchStartXRef = useRef(0);
	const dragStartXRef = useRef<number | null>(null);
	const suppressClickRef = useRef(false);

	const [index, setIndex] = useState(0);
	const [isAuto, setIsAuto] = useState(autoPlay);
	const [isPaused, setIsPaused] = useState(false);
	const [inView, setInView] = useState(false);
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		if (!autoPlay || prefersReducedMotion) setIsAuto(false);
	}, [autoPlay]);

	useEffect(() => {
		const element = containerRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
			threshold: 0.5,
		});
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const toManual = useCallback(() => {
		setIsAuto(false);
		setIsPaused(false);
		elapsedRef.current = 0;
	}, []);

	const goTo = useCallback(
		(next: number) => {
			if (next < 0 || next > count - 1) return;
			toManual();
			setIndex(next);
			if (next > index) onManualAdvance?.();
		},
		[count, index, toManual, onManualAdvance]
	);

	const goNext = useCallback(() => goTo(index + 1), [goTo, index]);
	const goPrevious = useCallback(() => goTo(index - 1), [goTo, index]);

	const isRunning = isAuto && !isPaused && inView && count > 1;

	useEffect(() => {
		if (!isRunning) return;

		let frame = 0;
		let last = performance.now();

		const tick = (now: number) => {
			elapsedRef.current += now - last;
			last = now;

			if (elapsedRef.current >= SLIDE_MS + DELAY_MS) {
				elapsedRef.current = 0;
				setProgress(0);
				setIndex(current => Math.min(current + 1, count - 1));
			} else {
				setProgress(Math.min(elapsedRef.current / SLIDE_MS, 1));
			}

			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [isRunning, count]);

	useEffect(() => {
		if (isAuto && index === count - 1 && count > 1) setIsAuto(false);
	}, [isAuto, index, count]);

	const togglePlayback = useCallback(() => {
		if (isAuto && !isPaused) {
			setIsPaused(true);
			return;
		}
		elapsedRef.current = 0;
		setProgress(0);
		setIsPaused(false);
		setIsAuto(true);
		if (index === count - 1) setIndex(0);
	}, [count, index, isAuto, isPaused]);

	const onTouchStart = useCallback((event: React.TouchEvent) => {
		touchStartXRef.current = event.touches[0].clientX;
	}, []);

	const onPointerDown = useCallback((event: React.PointerEvent) => {
		if (event.pointerType !== 'mouse' || event.button !== 0) return;
		dragStartXRef.current = event.clientX;
	}, []);

	const onPointerUp = useCallback(
		(event: React.PointerEvent) => {
			if (event.pointerType !== 'mouse' || dragStartXRef.current === null) return;
			const delta = event.clientX - dragStartXRef.current;
			dragStartXRef.current = null;
			if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
			suppressClickRef.current = true;
			if (delta < 0) goNext();
			else goPrevious();
		},
		[goNext, goPrevious]
	);

	const onClickCapture = useCallback((event: React.MouseEvent) => {
		if (!suppressClickRef.current) return;
		suppressClickRef.current = false;
		event.preventDefault();
		event.stopPropagation();
	}, []);

	// Cards are links wrapping images, both natively draggable: without this a mouse drag starts an
	// HTML5 drag-and-drop, the browser fires pointercancel instead of pointerup, and the slide never
	// advances.
	const onDragStart = useCallback((event: React.DragEvent) => event.preventDefault(), []);

	const onPointerCancel = useCallback(() => {
		dragStartXRef.current = null;
	}, []);

	const onTouchEnd = useCallback(
		(event: React.TouchEvent) => {
			const delta = event.changedTouches[0].clientX - touchStartXRef.current;
			if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
			if (delta < 0) goNext();
			else goPrevious();
		},
		[goNext, goPrevious]
	);

	return {
		containerRef,
		index,
		fill: isAuto ? progress : 1,
		isPlaying: isRunning,
		shouldPlayVideo: !isPaused && inView && (!isAuto || progress < 1),
		goTo,
		togglePlayback,
		onTouchStart,
		onTouchEnd,
		onPointerDown,
		onPointerUp,
		onPointerCancel,
		onClickCapture,
		onDragStart,
	};
}

export default useSlideshow;
